import React, { useEffect, useState } from "react";
import PanelDashboardLayout from "../PanelDashboardLayout";
import {
  getPromotionCohortsViaAdmin,
  getPromotionAcademicYearsViaAdmin,
  previewPromotionViaAdmin,
  commitPromotionViaAdmin,
  undoPromotionViaAdmin,
  getPromotionEventsViaAdmin,
} from "../../../utils/Api";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import UndoIcon from "@mui/icons-material/Undo";
import HistoryIcon from "@mui/icons-material/History";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

// Colors for backlog buckets and verdict outcomes
const outcomeColor = (outcome) => {
  switch ((outcome || "").toLowerCase()) {
    case "promoted":
      return "success";
    case "detained":
      return "error";
    case "graduated":
      return "info";
    default:
      return "default";
  }
};

const Promotions = () => {
  const [cohorts, setCohorts] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);

  // Promote dialog state
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [activeCohort, setActiveCohort] = useState(null);
  const [targetAYStart, setTargetAYStart] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);

  // Undo (rollback) state — keyed by cohort name so only the running row's button disables.
  const [undoLoading, setUndoLoading] = useState(null);

  // Events dialog state
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [eventsCohort, setEventsCohort] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cohortRes, ayRes] = await Promise.all([
        getPromotionCohortsViaAdmin(),
        getPromotionAcademicYearsViaAdmin(),
      ]);
      setCohorts(cohortRes?.cohorts || []);
      setAcademicYears(ayRes?.academicYears || []);
    } catch (error) {
      console.error("Error fetching promotion data:", error);
      toast.error(error?.response?.data?.error || "Error loading promotions data");
    } finally {
      setLoading(false);
    }
  };

  // Pick a sensible default target AY: the next/max startYear available
  const defaultTargetAYStart = () => {
    if (!academicYears.length) return "";
    const current = academicYears.find((ay) => ay.isCurrent);
    if (current) {
      const next = academicYears
        .filter((ay) => ay.startYear > current.startYear)
        .sort((a, b) => a.startYear - b.startYear)[0];
      if (next) return String(next.startYear);
      return String(current.startYear + 1);
    }
    const maxStart = Math.max(...academicYears.map((ay) => ay.startYear));
    return String(maxStart);
  };

  const openPromoteModal = (cohort) => {
    setActiveCohort(cohort);
    setPreviewData(null);
    setTargetAYStart(defaultTargetAYStart());
    setShowPromoteModal(true);
  };

  const closePromoteModal = () => {
    setShowPromoteModal(false);
    setActiveCohort(null);
    setPreviewData(null);
  };

  const handlePreview = async () => {
    if (!activeCohort || !targetAYStart) {
      toast.warning("Please select a target academic year");
      return;
    }
    setPreviewLoading(true);
    try {
      const response = await previewPromotionViaAdmin(
        activeCohort.name,
        Number(targetAYStart)
      );
      setPreviewData(response);
    } catch (error) {
      console.error("Error previewing promotion:", error);
      toast.error(error?.response?.data?.error || "Error previewing promotion");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleApply = async () => {
    if (!activeCohort || !targetAYStart) return;
    if (
      !window.confirm(
        "Are you sure you want to apply this promotion? This will update student enrollments and cannot be easily undone."
      )
    ) {
      return;
    }
    setApplyLoading(true);
    try {
      const response = await commitPromotionViaAdmin(
        activeCohort.name,
        Number(targetAYStart)
      );
      const s = response?.stats || {};
      toast.success(
        `Promotion applied: ${s.promoted || 0} promoted, ${s.detained || 0} held back, ${s.graduated || 0} graduated` +
          (s.skipped ? `, ${s.skipped} skipped` : "") +
          (s.failed ? `, ${s.failed} failed` : "")
      );
      closePromoteModal();
      fetchData();
    } catch (error) {
      console.error("Error applying promotion:", error);
      toast.error(error?.response?.data?.error || "Error applying promotion");
    } finally {
      setApplyLoading(false);
    }
  };

  // Undo the last promotion for a cohort. The target AY to roll back is the cohort's CURRENT
  // academic-year start — the year they were promoted INTO.
  const handleUndo = async (cohort) => {
    if (!cohort?.name || cohort?.currentAcademicYearStart == null) return;
    if (
      !window.confirm(
        "Undo the last promotion for this cohort? Students will be moved back to their previous year."
      )
    ) {
      return;
    }
    setUndoLoading(cohort.name);
    try {
      const response = await undoPromotionViaAdmin(
        cohort.name,
        Number(cohort.currentAcademicYearStart)
      );
      const s = response?.stats || {};
      toast.success(
        `Undone: ${s.reopened || 0} students moved back` +
          (s.reactivated ? `, ${s.reactivated} re-activated` : "") +
          (s.conflicts ? `, ${s.conflicts} conflicts` : "") +
          (s.failed ? `, ${s.failed} failed` : "")
      );
      fetchData();
    } catch (error) {
      console.error("Error undoing promotion:", error);
      toast.error(error?.response?.data?.error || "Error undoing promotion");
    } finally {
      setUndoLoading(null);
    }
  };

  const openEventsModal = async (cohort) => {
    setEventsCohort(cohort);
    setEvents([]);
    setShowEventsModal(true);
    setEventsLoading(true);
    try {
      const response = await getPromotionEventsViaAdmin(cohort.name);
      setEvents(response?.events || []);
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error(error?.response?.data?.error || "Error fetching audit events");
    } finally {
      setEventsLoading(false);
    }
  };

  const closeEventsModal = () => {
    setShowEventsModal(false);
    setEventsCohort(null);
    setEvents([]);
  };

  const tableCustomStyles = {
    headRow: {
      style: {
        color: "#fff",
        backgroundColor: "#0F1015",
      },
    },
  };

  const renderBacklogChips = (buckets) => {
    if (!buckets) return "-";
    return (
      <div className="flex flex-wrap gap-1">
        <Chip
          label={`Cleared: ${buckets.zero ?? 0}`}
          size="small"
          sx={{ backgroundColor: "#d4edda", color: "#155724" }}
        />
        <Chip
          label={`Has backlog: ${buckets.hasBacklog ?? 0}`}
          size="small"
          sx={{ backgroundColor: "#fff3cd", color: "#856404" }}
        />
      </div>
    );
  };

  const columns = [
    {
      name: "SL. No",
      selector: (row, index) => ++index,
      width: "70px",
    },
    {
      name: "Name",
      selector: (row) => row?.name,
      sortable: true,
      width: "150px",
    },
    {
      name: "Program",
      selector: (row) => row?.program?.name || "-",
      sortable: true,
      width: "150px",
    },
    {
      name: "Current Stage(s)",
      selector: (row) => (
        <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
          {Array.isArray(row?.currentYearStages) && row.currentYearStages.length
            ? row.currentYearStages.join(", ")
            : "-"}
        </div>
      ),
      width: "160px",
    },
    {
      name: "Students",
      selector: (row) => row?.currentStudentCount ?? 0,
      sortable: true,
      width: "100px",
    },
    {
      name: "Backlog",
      cell: (row) => renderBacklogChips(row?.backlogBuckets),
      width: "260px",
    },
    {
      name: "Status",
      cell: (row) =>
        row?.alreadyPromoted ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Chip
              label="Promoted"
              size="small"
              sx={{ backgroundColor: "#e0e0e0", color: "#555" }}
            />
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={
                undoLoading === row?.name ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <UndoIcon fontSize="small" />
                )
              }
              disabled={undoLoading === row?.name}
              onClick={() => handleUndo(row)}
            >
              {undoLoading === row?.name ? "Undoing..." : "Undo"}
            </Button>
          </Box>
        ) : (
          <button
            className="btn btn-sm btn-primary"
            onClick={() => openPromoteModal(row)}
          >
            <ArrowUpwardIcon fontSize="small" /> Promote
          </button>
        ),
      width: "180px",
    },
    {
      name: "Audit",
      cell: (row) => (
        <button
          className="btn btn-sm btn-outline-secondary"
          title="View promotion audit events"
          onClick={() => openEventsModal(row)}
        >
          <HistoryIcon fontSize="small" /> Events
        </button>
      ),
      width: "130px",
    },
  ];

  const policy = previewData?.policy;
  const summary = previewData?.summary;

  return (
    <PanelDashboardLayout>
      <div>
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">Promotions</h4>
              <p className="card-description">
                Promote cohorts to the next academic year. Preview the outcome
                per student before applying.
              </p>
              <div className="table-responsive">
                <DataTable
                  columns={columns}
                  data={cohorts}
                  customStyles={tableCustomStyles}
                  pagination
                  progressPending={loading}
                  noDataComponent={
                    <div className="p-4">No cohorts available for promotion.</div>
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* Promote / Preview Dialog */}
        <Dialog open={showPromoteModal} onClose={closePromoteModal} maxWidth="lg" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <ArrowUpwardIcon color="primary" />
              <span>Promote Cohort{activeCohort ? `: ${activeCohort.name}` : ""}</span>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Select the target academic year and preview how each student in this
              cohort will be promoted. Students advance every year regardless of
              backlogs; in the final year, only those who have cleared all backlogs
              graduate — the rest are held back until they clear them.
            </Typography>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 3, flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="target-ay-label">Target Academic Year</InputLabel>
                <Select
                  labelId="target-ay-label"
                  label="Target Academic Year"
                  value={targetAYStart}
                  onChange={(e) => setTargetAYStart(e.target.value)}
                >
                  {academicYears.map((ay) => (
                    <MenuItem key={ay._id} value={String(ay.startYear)}>
                      {ay.name}
                      {ay.isCurrent ? " (Current)" : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                color="primary"
                onClick={handlePreview}
                disabled={previewLoading || !targetAYStart}
                startIcon={previewLoading && <CircularProgress size={16} color="inherit" />}
              >
                {previewLoading ? "Loading Preview..." : "Preview"}
              </Button>
            </Box>

            {previewData && (
              <Box>
                {policy?.verificationStatus === "UNVERIFIED" && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    The backlog data for this program is <strong>UNVERIFIED</strong>.
                    Please confirm each student's backlog count with the exam cell
                    before applying this promotion.
                  </Alert>
                )}

                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Summary:</strong> {summary?.promoted || 0} promoted,{" "}
                  {summary?.detained || 0} held back, {summary?.graduated || 0} graduated.
                </Alert>

                <Box sx={{ border: "1px solid #ddd", borderRadius: 1 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      backgroundColor: "#f5f5f5",
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      Per-Student Verdicts ({previewData.verdicts?.length || 0})
                    </Typography>
                  </Box>
                  <Box sx={{ maxHeight: 360, overflowY: "auto", p: 1 }}>
                    <table className="table table-sm table-striped mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: 50 }}>#</th>
                          <th>Name</th>
                          <th>Roll No</th>
                          <th>From → To</th>
                          <th>Backlog</th>
                          <th>Outcome</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.verdicts?.map((v, idx) => (
                          <tr key={v.student || idx}>
                            <td>{idx + 1}</td>
                            <td>{v.name}</td>
                            <td>{v.rollNumber}</td>
                            <td>
                              {v.fromYearStage} &rarr; {v.toYearStage || "-"}
                            </td>
                            <td>{v.backlog}</td>
                            <td>
                              <Chip
                                label={v.outcome}
                                size="small"
                                color={outcomeColor(v.outcome)}
                              />
                            </td>
                            <td style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                              {v.reason}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={closePromoteModal} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleApply}
              disabled={!previewData || applyLoading}
              startIcon={applyLoading && <CircularProgress size={16} color="inherit" />}
            >
              {applyLoading ? "Applying..." : "Apply Promotion"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Audit Events Dialog */}
        <Dialog open={showEventsModal} onClose={closeEventsModal} maxWidth="lg" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <HistoryIcon color="primary" />
              <span>
                Promotion Audit Events{eventsCohort ? `: ${eventsCohort.name}` : ""}
              </span>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {eventsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : events.length === 0 ? (
              <div className="p-3">No committed promotion events for this cohort yet.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-striped mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}>#</th>
                      <th>Outcome</th>
                      <th>From → To</th>
                      <th>Backlog</th>
                      <th>Reason</th>
                      <th>Decided At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((ev, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>
                          <Chip label={ev.outcome} size="small" color={outcomeColor(ev.outcome)} />
                        </td>
                        <td>
                          {ev.fromYearStage} &rarr; {ev.toYearStage || "-"}
                        </td>
                        <td>{ev.backlogCountAtDecision}</td>
                        <td style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                          {ev.reason}
                        </td>
                        <td>
                          {ev.decidedAt ? new Date(ev.decidedAt).toLocaleString() : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={closeEventsModal} color="inherit">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </PanelDashboardLayout>
  );
};

export default Promotions;
